/**
 * Resolution & print-quality helpers.
 * Dimensions must come from intrinsic image pixels, never CSS/DOM layout size.
 * Mirror of backend printPlan.js for UI preview — server remains authoritative.
 */

export type ImageResolution = {
  width: number;
  height: number;
  megapixels: number;
  aspectRatio: number;
};

export type PrintSizeId = '4x6' | '5x7' | '8x10' | '11x14';

export type PrintQualityRating =
  | 'Excellent'
  | 'Good'
  | 'Acceptable'
  | 'Upscale recommended'
  | 'Insufficient';

export type PrintSizeGuide = {
  id: PrintSizeId;
  label: string;
  widthIn: number;
  heightIn: number;
  printWidthIn: number;
  printHeightIn: number;
  /** Effective PPI after cover-fit crop. */
  effectiveDpi: number;
  requiresCrop: boolean;
  rating: PrintQualityRating;
  upscaleRequired: boolean;
  recommendedFactor: number | null;
  projectedEffectivePpi: number | null;
  canPrepare: boolean;
  message: string;
};

export type PrintQualitySummary = {
  estimatedAt300Dpi: { widthIn: number; heightIn: number };
  sizes: PrintSizeGuide[];
};

export const TARGET_PPI = 300;
export const UPSCALE_FACTORS = [2, 3, 4] as const;

export const COMMON_PRINT_SIZES: Array<{
  id: PrintSizeId;
  label: string;
  widthIn: number;
  heightIn: number;
}> = [
  { id: '4x6', label: '4×6', widthIn: 4, heightIn: 6 },
  { id: '5x7', label: '5×7', widthIn: 5, heightIn: 7 },
  { id: '8x10', label: '8×10', widthIn: 8, heightIn: 10 },
  { id: '11x14', label: '11×14', widthIn: 11, heightIn: 14 },
];

export function megapixelsOf(width: number, height: number): number {
  return Number(((width * height) / 1_000_000).toFixed(2));
}

export function toImageResolution(width: number, height: number): ImageResolution | null {
  const w = Math.round(Number(width));
  const h = Math.round(Number(height));
  if (!Number.isFinite(w) || !Number.isFinite(h) || w < 1 || h < 1) return null;
  return {
    width: w,
    height: h,
    megapixels: megapixelsOf(w, h),
    aspectRatio: Number((w / h).toFixed(4)),
  };
}

/** Load intrinsic pixel dimensions from an image URL/data URL. */
export function measureImageResolution(src: string): Promise<ImageResolution> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const res = toImageResolution(img.naturalWidth || img.width, img.naturalHeight || img.height);
      if (!res) {
        reject(new Error('Could not read image dimensions'));
        return;
      }
      resolve(res);
    };
    img.onerror = () => reject(new Error('Failed to load image for dimension measurement'));
    img.src = src;
  });
}

export function estimatedPrintInchesAtDpi(
  width: number,
  height: number,
  dpi = 300
): { widthIn: number; heightIn: number } {
  return {
    widthIn: Number((width / dpi).toFixed(1)),
    heightIn: Number((height / dpi).toFixed(1)),
  };
}

export function orientedPrintInches(
  widthPx: number,
  heightPx: number,
  size: { widthIn: number; heightIn: number }
): { printWidthIn: number; printHeightIn: number } {
  if (widthPx >= heightPx) {
    return { printWidthIn: size.heightIn, printHeightIn: size.widthIn };
  }
  return { printWidthIn: size.widthIn, printHeightIn: size.heightIn };
}

export function effectivePpi(
  widthPx: number,
  heightPx: number,
  printWidthIn: number,
  printHeightIn: number
): number {
  return Number((Math.min(widthPx / printWidthIn, heightPx / printHeightIn)).toFixed(1));
}

export function rateDpi(dpi: number): PrintQualityRating {
  if (dpi >= 300) return 'Excellent';
  if (dpi >= 240) return 'Good';
  if (dpi >= 200) return 'Acceptable';
  if (dpi >= 150) return 'Upscale recommended';
  return 'Insufficient';
}

export function recommendFactorForPrint(
  widthPx: number,
  heightPx: number,
  printWidthIn: number,
  printHeightIn: number
): {
  upscaleRequired: boolean;
  recommendedFactor: number | null;
  currentEffectivePpi: number;
  projectedEffectivePpi: number;
  canPrepare: boolean;
  message: string;
} {
  const current = effectivePpi(widthPx, heightPx, printWidthIn, printHeightIn);
  if (current >= TARGET_PPI) {
    return {
      upscaleRequired: false,
      recommendedFactor: null,
      currentEffectivePpi: current,
      projectedEffectivePpi: current,
      canPrepare: true,
      message: 'Already meets Kinvera’s 300 PPI print target.',
    };
  }
  for (const factor of UPSCALE_FACTORS) {
    const projected = effectivePpi(widthPx * factor, heightPx * factor, printWidthIn, printHeightIn);
    if (projected >= TARGET_PPI) {
      return {
        upscaleRequired: true,
        recommendedFactor: factor,
        currentEffectivePpi: current,
        projectedEffectivePpi: projected,
        canPrepare: true,
        message: `Prepare with ${factor}× enhancement to reach ~${Math.round(projected)} PPI.`,
      };
    }
  }
  const projected = effectivePpi(widthPx * 4, heightPx * 4, printWidthIn, printHeightIn);
  if (projected >= 200) {
    return {
      upscaleRequired: true,
      recommendedFactor: 4,
      currentEffectivePpi: current,
      projectedEffectivePpi: projected,
      canPrepare: true,
      message: `Even with 4×, expect about ${Math.round(projected)} PPI. A smaller print size will look better.`,
    };
  }
  return {
    upscaleRequired: true,
    recommendedFactor: null,
    currentEffectivePpi: current,
    projectedEffectivePpi: projected,
    canPrepare: false,
    message: `This photo is too small for a high-quality print at this size. Choose a smaller print size.`,
  };
}

/**
 * Rate common print sizes using cover-fit (crop-aware) effective PPI.
 */
export function evaluatePrintQuality(width: number, height: number): PrintQualitySummary {
  const w = Number(width);
  const h = Number(height);
  const sizes: PrintSizeGuide[] = COMMON_PRINT_SIZES.map((size) => {
    const oriented = orientedPrintInches(w, h, size);
    const imgAr = w / h;
    const printAr = oriented.printWidthIn / oriented.printHeightIn;
    const requiresCrop = Math.abs(imgAr - printAr) > 0.03;
    const coverDpi = Math.round(
      effectivePpi(w, h, oriented.printWidthIn, oriented.printHeightIn)
    );
    const rec = recommendFactorForPrint(w, h, oriented.printWidthIn, oriented.printHeightIn);

    return {
      id: size.id,
      label: size.label,
      widthIn: size.widthIn,
      heightIn: size.heightIn,
      printWidthIn: oriented.printWidthIn,
      printHeightIn: oriented.printHeightIn,
      effectiveDpi: coverDpi,
      requiresCrop,
      rating: rateDpi(coverDpi),
      upscaleRequired: rec.upscaleRequired,
      recommendedFactor: rec.recommendedFactor,
      projectedEffectivePpi: rec.projectedEffectivePpi,
      canPrepare: rec.canPrepare,
      message: rec.message,
    };
  });

  return {
    estimatedAt300Dpi: estimatedPrintInchesAtDpi(w, h, 300),
    sizes,
  };
}

export function projectFactorOutput(
  width: number,
  height: number,
  factor: number,
  maxOutputPixels?: number | null
): {
  outputWidth: number;
  outputHeight: number;
  outputPixels: number;
  megapixels: number;
  withinLimit: boolean;
} {
  const outputWidth = Math.round(width * factor);
  const outputHeight = Math.round(height * factor);
  const outputPixels = outputWidth * outputHeight;
  const hasLimit =
    maxOutputPixels != null && Number.isFinite(maxOutputPixels) && maxOutputPixels > 0;
  return {
    outputWidth,
    outputHeight,
    outputPixels,
    megapixels: megapixelsOf(outputWidth, outputHeight),
    withinLimit: hasLimit ? outputPixels <= maxOutputPixels! : true,
  };
}
